import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { contains, routesAction, scrollUp } from '../../shared/helpers';
import {
  ManagedPopup,
  MarketplacePopup,
  ContactInformationPopup,
} from '../../components/';
import { OverviewStep } from '../../components/OverviewStep';
import { setCurrentOnboardingStep } from '../../store/actions/Onboarding';
import { saveProgress } from '../../store/actions/Dashboard';
import { setCurrentOverviewStep } from '../../store/actions/Overview';
import { openDialog, closeDialog } from '../../store/actions/Dialog';
import { EndingEarlyPopup } from '../../components/Popup/EndingEarlyPopup';

const filters = {
  secondStepDone: step =>
    step.key === 'project_materials' && step.status === 'done',
  thirdStepDone: step => step.status === 'done' && !step.disabled,
  emptyThirdStep: step => !_.isEmpty(step.selectedValue),
};

class Overview extends Component {
  constructor(props) {
    super(props);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handleGoToAnswer = this.handleGoToAnswer.bind(this);
    this.handleAddContactInfo = this.handleAddContactInfo.bind(this);
    this.openContactInformationPopup = this.openContactInformationPopup.bind(
      this,
    );
  }

  componentDidMount() {
    this.showMidpointModal();
  }

  hasManagedAnswer = steps =>
    !_(steps)
      .flatMap(step => step.onboardingSteps)
      .map(step =>
        step.cards.find(card => step.selectedValue.includes(card.value)),
      )
      .filter(card => (card ? card.is_hand : false))
      .isEmpty();

  showMidpointModal() {
    const {
      currentOverviewSteps,
      onboardingFullSteps,
      dispatchCloseDialog,
    } = this.props;

    const {
      basic_information: basicInformation,
      project_materials: projectMaterials,
      additional_requirements: additionalRequirements,
    } = onboardingFullSteps;

    const isSecondStepDone = contains(
      currentOverviewSteps,
      filters.secondStepDone,
    );

    const isThirdStepEmpty = !contains(
      additionalRequirements.onboardingSteps,
      filters.emptyThirdStep,
    );

    const isThirdStepDone = contains(
      additionalRequirements.onboardingSteps,
      filters.thirdStepDone,
    );

    if (isSecondStepDone && isThirdStepEmpty) {
      const isManaged = this.hasManagedAnswer([
        basicInformation,
        projectMaterials,
      ]);

      const managedPopup = {
        id: 'managed-popup',
        body: (
          <ManagedPopup
            handleTalkToRepresentative={() => {
              setTimeout(() => {
                this.openContactInformationPopup();
              });
            }}
            handleContinueFilling={dispatchCloseDialog}
          />
        ),
      };

      const marketplacePopup = {
        id: 'marketplace-popup',
        body: (
          <MarketplacePopup
            handleTalkToRepresentative={() => {
              setTimeout(() => {
                this.openContactInformationPopup();
              });
            }}
            handleContinueFilling={dispatchCloseDialog}
          />
        ),
      };

      const popup = isManaged ? managedPopup : marketplacePopup;

      this.props.dispatchOpenDialog(popup);
    }

    this.updateManagementWay(isThirdStepDone);
  }

  openContactInformationPopup() {
    const { dispatchOpenDialog } = this.props;

    const popup = {
      id: 'contact-information-popup',
      body: (
        <ContactInformationPopup
          handleAddContactInfo={this.handleAddContactInfo}
          handleNextStep={() => {}}
        />
      ),
    };

    dispatchOpenDialog(popup);
  }

  handleAddContactInfo(info) {
    const {
      match: {
        params: { project_id: projectId },
      },
      history,
      dispatchSaveProgress,
      dispatchCloseDialog,
      dispatchOpenDialog,
    } = this.props;

    // eslint-disable-next-line camelcase
    const { means_of_contact, phone_number, email } = info;

    dispatchSaveProgress(
      {
        means_of_contact,
        phone_number,
        email,
      },
      projectId,
    ).then(() => {
      dispatchOpenDialog(
        {
          id: 'summary-popup',
          body: (
            <EndingEarlyPopup
              handleCloseDialog={() => {
                dispatchCloseDialog();
                history.push(routesAction.default());
              }}
            />
          ),
        },
        {
          closeOnEscape: false,
          closeOnDimmerClick: false,
          closable: 'true',
        },
      );
    });
  }

  updateManagementWay(isThirdStepDone = false) {
    if (!isThirdStepDone) return;

    const {
      onboardingFullSteps: {
        basic_information: basicInformation,
        project_materials: projectMaterials,
        additional_requirements: additionalRequirements,
      },
      match: {
        params: { project_id: projectId },
      },
      currentOnboardingStep,
      currentOverviewStep,
    } = this.props;

    const isCompulsoryManaged = this.hasManagedAnswer([
      basicInformation,
      projectMaterials,
      additionalRequirements,
    ]);

    this.props.dispatchSaveProgress(
      {
        way_to_manage: isCompulsoryManaged
          ? 'compulsory_managed'
          : 'marketplace',
        current_onboarding_step: currentOnboardingStep,
        current_overview_step: currentOverviewStep,
      },
      projectId,
    );
  }

  handleNextStep(event) {
    event.preventDefault();
    const {
      history: { push },
      match: {
        params: { project_id: projectId },
      },
    } = this.props;
    push(routesAction.onboarding(projectId));
  }

  handleGoToAnswer(overviewStep, onboardingStep) {
    this.props.dispatchGoToQuestionStep(overviewStep, onboardingStep);
    this.props.history.push(
      routesAction.onboarding(this.props.match.params.project_id),
    );
    scrollUp();
  }

  render() {
    const {
      currentOverviewStep,
      currentOverviewSteps,
      currentOnboardingSteps,
      onboardingFullSteps,
    } = this.props;

    return (
      <div>
        <OverviewStep
          currentStep={currentOverviewStep}
          currentOnboardingSteps={currentOnboardingSteps}
          currentOverviewSteps={currentOverviewSteps}
          handleNextStep={this.handleNextStep}
          handleGoToAnswer={this.handleGoToAnswer}
          onboardingFullSteps={onboardingFullSteps}
        />
      </div>
    );
  }
}

Overview.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      project_id: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  currentOverviewStep: PropTypes.string,
  currentOnboardingStep: PropTypes.string,
  currentOverviewSteps: PropTypes.array,
  currentOnboardingSteps: PropTypes.array,
  onboardingFullSteps: PropTypes.object,
  dispatchGoToQuestionStep: PropTypes.func,
  dispatchOpenDialog: PropTypes.func,
  dispatchCloseDialog: PropTypes.func,
  dispatchSaveProgress: PropTypes.func,
};

const mapStateToProps = state => {
  const { currentStep, overviewSteps } = state.overview;
  const currentOnboardingSteps = state.onboarding[currentStep].onboardingSteps;
  const onboardingFullSteps = state.onboarding;
  const { projects } = state.dashboard;
  const { user } = state.authentication;

  return {
    currentOverviewSteps: overviewSteps,
    currentOverviewStep: currentStep,
    currentOnboardingStep: state.onboarding.currentStep,
    currentOnboardingSteps,
    onboardingFullSteps,
    projects,
    user,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatchGoToQuestionStep: (overviewStep, onboardingStep) => {
    dispatch(setCurrentOverviewStep(overviewStep));
    dispatch(setCurrentOnboardingStep(onboardingStep));
  },
  dispatchOpenDialog: content => {
    dispatch(openDialog(content));
  },
  dispatchCloseDialog: () => {
    dispatch(closeDialog());
  },
  dispatchSaveProgress: (saveinfo, projectId, isFetchingProjects) =>
    dispatch(saveProgress(saveinfo, projectId, isFetchingProjects)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Overview);



// WEBPACK FOOTER //
// ./src/containers/Overview/Overview.js